/**
 * Reconnect resync: broadcast events missed while the Echo socket was down
 * are gone for good — RTK Query's refetchOnReconnect only fires on browser
 * online/offline transitions, not on a Reverb-side drop while the network
 * stays up (backend restart, idle proxy cut, expired channel auth). When
 * the socket comes back after an outage, pull exactly the missed window
 * (GET /api/realtime/missed?since=<outage start>, recorded by echoClient)
 * and invalidate every tag the realtime handlers feed so mounted views
 * refetch instead of staying stale until the next event arrives.
 */
import { store } from '@/redux/store'
import { apiSlice } from '@/redux/api/apiSlice'
import { getAndClearRealtimeOutageSince, onRealtimeResync } from '@/services/echoClient'
import api from '@/services/api'

// Union of the tags the realtime hooks invalidate per event (see
// useStoreRealtimeRefresh / useTenantChannelRealtime /
// useAccountingRealtimeRefresh / usePosWebSockets / useAiApprovals).
// Invalidation is cheap for tags no mounted query uses.
const REALTIME_FED_TAGS = [
  'Sales',
  'Purchases',
  'SaleReturn',
  'Products',
  'Quotations',
  'DashboardStats',
  'User',
  'TenantUser',
  'Role',
  'AiUsage',
  'Accounting',
  'AccountLedger',
  'ManualJournal',
  'GLInquiry',
  'AccruedRevenue',
  'ControlAccountReconciliation',
  'FixedAsset',
  'DepreciationSchedule',
  'FiscalPeriod',
] as const

export function installRealtimeResync(): void {
  onRealtimeResync(() => {
    // Best-effort missed-window pull: the invalidation below is the actual
    // recovery (mounted queries refetch); the pull warms the replay buffer
    // read path and surfaces the missed count for telemetry. A failed pull
    // (offline auth, 404 tenant) must never block the invalidation.
    const since = getAndClearRealtimeOutageSince()
    if (since) {
      // Awaited inside its own try/catch: a silent `.catch(() => null)` made a
      // failed pull undiagnosable (offline auth, 404 tenant, 401) even though
      // the invalidation below is what actually recovers.
      void (async () => {
        try {
          await api.get('/realtime/missed', { params: { since } })
          // TODO: emit telemetry for the missed count once the channel exists.
        } catch (err) {
          console.warn('[Realtime] missed-window pull failed; invalidating tags anyway.', err)
        }
      })()
    }
    store.dispatch(apiSlice.util.invalidateTags([...REALTIME_FED_TAGS]))
  })
}
